import { defaultTheme } from '@automata/mui-theme';
import {
  DurationPickerV2 as DurationPicker,
  NumberField,
  TextField,
  Typography,
} from '@automata/ui';
import { getTimeBreakdownStrings } from '@automata/ui/src/DurationPickerV2/utils';
import {
  BooleanParameterDefinition,
  IntegerParameterDefinition,
  ParameterDefinition,
  StringParameterDefinition,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import {
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { Box, IconButton, Stack, Switch, SxProps } from '@mui/material';
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useToggle } from 'usehooks-ts';

import { DeleteParameterReferenceModal } from './DeleteParameterReferenceModal';
import { NewParameterReferenceModal } from './NewParameterReferenceModal';
import { ParameterRangePopover } from './ParameterRangePopover';
import { NumberParameterDefinition, ParameterType } from './types';
import { allParameterTypes, getParameterIcon, getParameterType } from './utils';
import { selectAppliedParameterDefinitions } from '../../../features/workflow/selectors';
import { removeParameterDefinition } from '../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';

const styles = {
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: defaultTheme.palette.text.primary,
  },
  '& .Mui-disabled+.MuiSwitch-track': {
    opacity: 0.5,
  },
} satisfies SxProps;

type CellParameterDefinition = ParameterDefinition & { __reorder__: string };

interface DefaultCellRendererProps<T extends GridValidRowModel> {
  params: GridRenderCellParams<T>;
}

export const TypeCellRenderer = (
  params: GridRenderCellParams<ParameterDefinition>
) => {
  const parameterType = getParameterType(params.row);
  return (
    <Box display="flex" height="100%" alignItems="center">
      {getParameterIcon(parameterType)}
      <Typography marginLeft={1} variant="body2">
        {parameterType}
      </Typography>
    </Box>
  );
};

export const NameCellRenderer = (
  params: GridRenderCellParams<CellParameterDefinition>
) => (
  <Stack display="flex" justifyContent="center" height="100%">
    <TextField
      id="parameter-name"
      disabled={true}
      sx={styles}
      value={params.value}
    />
  </Stack>
);

const DefaultStringCellRenderer = ({
  params,
}: {
  params: GridRenderCellParams<StringParameterDefinition>;
}) => (
  <Stack display="flex" justifyContent="center" height="100%">
    <TextField
      disabled={true}
      id="parameter-default-text"
      sx={styles}
      value={params.value}
    />
  </Stack>
);

const DefaultBooleanCellRenderer = ({
  params,
}: DefaultCellRendererProps<BooleanParameterDefinition>) => (
  <Stack display="flex" justifyContent="center" height="100%">
    <Switch
      id="parameter-default-boolean"
      color="primary"
      disabled={true}
      sx={styles}
      checked={params.value ?? false}
    />
  </Stack>
);

const DefaultDurationCellRenderer = ({
  params,
}: DefaultCellRendererProps<IntegerParameterDefinition>) => {
  const timeBreakdown = getTimeBreakdownStrings(params.value);
  return (
    <Stack display="flex" justifyContent="center" height="100%">
      <DurationPicker
        id="parameter-default-duration"
        hours={timeBreakdown.hours}
        minutes={timeBreakdown.minutes}
        seconds={timeBreakdown.seconds}
        disabled={true}
        sx={styles}
      />
    </Stack>
  );
};

const DefaultNumberCellRenderer = ({
  params,
}: DefaultCellRendererProps<NumberParameterDefinition>) => (
  <Stack flexDirection="row" height="100%" alignItems="center" gap={1}>
    <NumberField
      id="parameter-default-number"
      decimal={params.row.type === 'float'}
      disabled={true}
      fullWidth
      sx={styles}
      value={params.value}
    />
    <ParameterRangePopover
      decimal={params.row.type === 'float'}
      max={params.row.maximum ?? null}
      min={params.row.minimum ?? null}
    />
  </Stack>
);

export const DefaultValueCellRenderer = (
  params: GridRenderCellParams<CellParameterDefinition>
) => {
  const parameterType = getParameterType(params.row);

  switch (parameterType) {
    case ParameterType.BOOLEAN:
      return (
        <DefaultBooleanCellRenderer
          params={params as GridRenderCellParams<BooleanParameterDefinition>}
        />
      );
    case ParameterType.INTEGER:
    case ParameterType.FLOAT:
      return (
        <DefaultNumberCellRenderer
          params={params as GridRenderCellParams<NumberParameterDefinition>}
        />
      );
    case ParameterType.DURATION:
      return (
        <DefaultDurationCellRenderer
          params={params as GridRenderCellParams<IntegerParameterDefinition>}
        />
      );
    default:
      return (
        <DefaultStringCellRenderer
          params={params as GridRenderCellParams<StringParameterDefinition>}
        />
      );
  }
};

export const ActionCellRenderer = (
  params: GridRenderCellParams<CellParameterDefinition>
) => {
  const { __reorder__, ...parameter } = params.row;
  const dispatch = useAppDispatch();
  const appliedParameters = useAppSelector(selectAppliedParameterDefinitions);
  const isParameterApplied = appliedParameters.some(
    (param) => param.id === params.row.id
  );

  const [showNewParameterReferenceModal, handleNewParameterReferenceModal] =
    useToggle();
  const [showDeleteParameterModal, handleDeleteParameterModal] = useToggle();

  const handleDeleteParameter = useCallback(() => {
    dispatch(removeParameterDefinition({ id: params.row.id }));
  }, [params.row.id, dispatch]);

  const handleModalOpen = useCallback(() => {
    if (isParameterApplied) {
      handleDeleteParameterModal();
      return;
    }

    handleDeleteParameter();
  }, [isParameterApplied, handleDeleteParameter, handleDeleteParameterModal]);

  return (
    <>
      <IconButton onClick={handleNewParameterReferenceModal}>
        <EditOutlinedIcon />
      </IconButton>
      <IconButton onClick={handleModalOpen}>
        <DeleteOutlineOutlinedIcon />
      </IconButton>
      {showNewParameterReferenceModal && (
        <NewParameterReferenceModal
          isEdit={true}
          parameter={parameter}
          typeOptions={allParameterTypes}
          onClose={handleNewParameterReferenceModal}
        />
      )}
      {showDeleteParameterModal && (
        <DeleteParameterReferenceModal
          parameter={parameter}
          onClose={handleDeleteParameterModal}
          onDelete={handleDeleteParameter}
        />
      )}
    </>
  );
};
